<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-truck-delivery-outline"
                title="Purchase Returns"
                subtitle="Goods sent back to vendors against a purchase order"
                style="width: 100%"
              >
                <v-tooltip bottom v-if="can('PURCHASE_RETURNS.CREATE')">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      fab
                      x-small
                      color="primary"
                      @click="openCreate()"
                    >
                      <v-icon dark> mdi-plus </v-icon>
                    </v-btn>
                  </template>
                  <span>New Purchase Return</span>
                </v-tooltip>
              </AppPageHeader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search Return #, PO # or Vendor... "
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="4">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.vendor_id"
                    :items="vendors"
                    item-text="name"
                    item-value="id"
                    label="Vendor"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-domain"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="500"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Return #</th>
                      <th class="text-left">Return Date</th>
                      <th class="text-left">Vendor</th>
                      <th class="text-left">PO Number</th>
                      <th class="text-right">Qty</th>
                      <th class="text-right">Amount</th>
                      <th class="text-left">Settlement</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="returns.length != 0">
                    <tr
                      v-for="(ret, i) in returns"
                      :key="`pr_${i}`"
                      class="clickable-row"
                      @click="viewReturn(ret)"
                    >
                      <td style="min-width: 85px">{{ (page - 1) * rowsPerPage + i + 1 }}</td>
                      <td>{{ ret.return_number }}</td>
                      <td>{{ ret.return_date | niceDate }}</td>
                      <td>{{ ret.vendor ? ret.vendor.name : "" }}</td>
                      <td>{{ ret.purchase_order ? ret.purchase_order.po_number : "" }}</td>
                      <td class="text-right">{{ totalQty(ret) }}</td>
                      <td class="text-right mono-figure">₹{{ ret.total_amount }}</td>
                      <td>
                        <v-chip
                          small
                          :color="ret.settlement_type === 1 ? 'success' : 'primary'"
                          text-color="white"
                        >
                          {{ settlementLabel(ret) }}
                        </v-chip>
                      </td>
                      <td class="text-right action_th">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="my-auto"
                              color="primary"
                              @click.stop="viewReturn(ret)"
                            >
                              <v-icon dark>mdi-eye</v-icon>
                            </v-btn>
                          </template>
                          <span>View this return</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="can('PURCHASE_RETURNS.UPDATE')">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              class="ml-2 my-auto"
                              fab
                              x-small
                              color="orange"
                              @click.stop="openEdit(ret)"
                            >
                              <v-icon dark>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit this return</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="can('PURCHASE_RETURNS.DELETE')">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              class="ml-2 my-auto"
                              fab
                              x-small
                              color="error"
                              @click.stop="confirmDelete(ret)"
                            >
                              <v-icon dark>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete this return (stock comes back)</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="9">
                        <AppEmptyState
                          icon="mdi-truck-delivery-outline"
                          title="No purchase returns yet"
                          message="Return damaged or extra goods to a vendor against the purchase order they came on."
                          :action-text="can('PURCHASE_RETURNS.CREATE') ? 'New Purchase Return' : ''"
                          @action="openCreate()"
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- New / edit purchase return -->
    <AppDialog
      :show.sync="showCreateDialog"
      persistent
      max-width="1000"
      icon="mdi-keyboard-return"
      :title="editingId ? `Edit ${editingNumber}` : 'New Purchase Return'"
      :subtitle="editingId
        ? 'Change the quantities, date or settlement - only the difference moves in stock'
        : 'Send goods back to the vendor - they are taken out of company stock'"
    >
      <v-row dense>
        <v-col md="4" cols="12">
          <v-autocomplete
            v-model="form.vendor_id"
            :items="vendors"
            item-text="name"
            item-value="id"
            outlined
            dense
            label="Vendor"
            clearable
            :disabled="!!editingId"
            @change="onVendorChange"
          ></v-autocomplete>
        </v-col>
        <v-col md="4" cols="12">
          <v-autocomplete
            v-model="form.purchase_order_id"
            :items="purchaseOrders"
            :item-text="poLabel"
            item-value="id"
            outlined
            dense
            :disabled="!!editingId"
            :loading="isLoadingPos"
            :error-messages="errors.purchase_order_id"
            @change="loadReturnable"
          >
            <template v-slot:label>Purchase Order <span style="color:#f44336;font-weight:bold;">*</span></template>
          </v-autocomplete>
        </v-col>
        <v-col md="4" cols="12">
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.return_date"
                outlined
                dense
                readonly
                label="Return Date"
                v-bind="attrs"
                v-on="on"
                :error-messages="errors.return_date"
              ></v-text-field>
            </template>
            <v-date-picker v-model="form.return_date" @input="dateMenu = false"></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <section v-if="isLoadingItems" class="py-6">
        <v-progress-linear indeterminate color="primary"></v-progress-linear>
      </section>
      <v-simple-table v-else-if="returnableItems.length" class="striped-table mb-4" dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Product</th>
              <th class="text-right">Ordered</th>
              <th class="text-right">Already Returned</th>
              <th class="text-right">In Stock</th>
              <th class="text-right">Unit Cost</th>
              <th class="text-right">GST %</th>
              <th class="text-right" style="width: 140px">Return Qty</th>
              <th class="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in returnableItems" :key="`ri_${item.id}`">
              <td>
                <div class="font-weight-medium">{{ item.product ? item.product.name : "" }}</div>
                <div class="text-caption grey--text">{{ variantLabel(item.product_variant) }}</div>
              </td>
              <td class="text-right">{{ item.ordered_quantity }}</td>
              <td class="text-right">{{ item.returned_quantity }}</td>
              <td class="text-right">{{ item.stock_quantity }}</td>
              <td class="text-right mono-figure">₹{{ item.purchase_price }}</td>
              <td class="text-right">{{ item.gst_percentage || 0 }}</td>
              <td class="text-right">
                <v-text-field
                  v-model.number="item.return_qty"
                  type="number"
                  min="0"
                  :max="item.returnable_quantity"
                  dense
                  hide-details="auto"
                  outlined
                  :disabled="item.returnable_quantity <= 0"
                  :error-messages="item.return_qty > item.returnable_quantity ? `Max ${item.returnable_quantity}` : ''"
                ></v-text-field>
              </td>
              <td class="text-right mono-figure">₹{{ lineAmount(item).toFixed(2) }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <div v-else-if="form.purchase_order_id" class="grey--text py-4">Nothing on this purchase order can be returned.</div>

      <v-row dense>
        <v-col md="6" cols="12">
          <v-textarea
            v-model="form.reason"
            outlined
            dense
            rows="2"
            auto-grow
            label="Reason (e.g. damaged, wrong size, excess)"
          ></v-textarea>
        </v-col>
        <v-col md="6" cols="12">
          <v-radio-group v-model="form.settlement_type" row dense class="mt-0" hide-details>
            <v-radio label="Adjust against vendor dues" :value="0"></v-radio>
            <v-radio label="Refund received" :value="1"></v-radio>
          </v-radio-group>
          <v-row dense v-if="form.settlement_type === 1" class="mt-2">
            <v-col cols="6">
              <v-select
                v-model="form.refund_mode"
                :items="paymentModes"
                item-text="text"
                item-value="value"
                outlined
                dense
                :error-messages="errors.refund_mode"
              >
                <template v-slot:label>Refund Mode <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-select>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.reference_number"
                outlined
                dense
                label="Reference (optional)"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <div class="d-flex justify-end text-subtitle-1">
        <div class="mr-6">Subtotal: <span class="mono-figure">₹{{ totals.subtotal.toFixed(2) }}</span></div>
        <div class="mr-6">GST: <span class="mono-figure">₹{{ totals.gst.toFixed(2) }}</span></div>
        <div class="font-weight-bold">Return Total: <span class="mono-figure">₹{{ totals.total.toFixed(2) }}</span></div>
      </div>

      <template v-slot:actions>
        <v-btn color="primary" depressed :loading="isSaving" :disabled="!canSave" @click="save">
          {{ editingId ? "Save Changes" : "Create Return" }}
        </v-btn>
      </template>
    </AppDialog>

    <!-- View a return -->
    <AppDialog
      :show.sync="showViewDialog"
      max-width="850"
      icon="mdi-keyboard-return"
      :title="viewing ? viewing.return_number : ''"
      :subtitle="viewing && viewing.vendor ? viewing.vendor.name : ''"
      :status-text="viewing ? settlementLabel(viewing) : ''"
      status-color="success"
    >
      <template v-slot:header-actions v-if="viewing && can('PURCHASE_RETURNS.UPDATE')">
        <v-btn small depressed color="white" class="primary--text" @click="(showViewDialog = false), openEdit(viewing)">
          <v-icon left small>mdi-pencil</v-icon> Edit
        </v-btn>
      </template>
      <div v-if="viewing">
        <v-row dense class="mb-2">
          <v-col cols="6" md="3">
            <div class="text-caption grey--text">Return Date</div>
            <div>{{ viewing.return_date | niceDate }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption grey--text">Purchase Order</div>
            <div>{{ viewing.purchase_order ? viewing.purchase_order.po_number : "" }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption grey--text">Settlement</div>
            <div>{{ settlementLabel(viewing) }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption grey--text">Created By</div>
            <div>{{ userName(viewing.created_by) }}</div>
          </v-col>
          <v-col cols="12" v-if="viewing.reason">
            <div class="text-caption grey--text">Reason</div>
            <div>{{ viewing.reason }}</div>
          </v-col>
        </v-row>
        <v-simple-table class="striped-table" dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Product</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Unit Cost</th>
                <th class="text-right">GST</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in viewing.items" :key="`vi_${item.id}`">
                <td>
                  <div class="font-weight-medium">{{ item.product ? item.product.name : "" }}</div>
                  <div class="text-caption grey--text">{{ variantLabel(item.product_variant) }}</div>
                </td>
                <td class="text-right">{{ item.quantity }}</td>
                <td class="text-right mono-figure">₹{{ item.purchase_price }}</td>
                <td class="text-right mono-figure">₹{{ item.gst_amount }}</td>
                <td class="text-right mono-figure">₹{{ item.line_total }}</td>
              </tr>
              <tr>
                <td colspan="4" class="text-right font-weight-bold">Total</td>
                <td class="text-right font-weight-bold mono-figure">₹{{ viewing.total_amount }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </AppDialog>

    <!-- Delete -->
    <v-dialog v-model="showDeleteDialog" max-width="420">
      <v-card v-if="toDelete">
        <v-card-title class="text-subtitle-1 font-weight-bold">Delete {{ toDelete.return_number }}?</v-card-title>
        <v-card-text>
          The {{ totalQty(toDelete) }} item(s) on this return will be added back to company stock.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" depressed :loading="isDeleting" @click="deleteReturn">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";

const today = () => new Date().toISOString().substr(0, 10);

export default {
  name: "purchaseReturnsPage",
  components: { AppDialog, AppEmptyState, AppPageHeader },
  data() {
    return {
      filter: { vendor_id: "", search_keyword: null },
      page: 1,
      paginationLength: 0,
      count: 0,
      returns: [],
      vendors: [],
      isLoading: false,
      paymentModes: [
        { text: "Cash", value: 0 },
        { text: "Bank Transfer", value: 1 },
        { text: "Cheque", value: 2 },
        { text: "UPI", value: 3 },
        { text: "Other", value: 4 },
      ],
      // create
      showCreateDialog: false,
      editingId: null, // set when the dialog edits an existing return
      editingNumber: "",
      dateMenu: false,
      form: this.emptyForm(),
      errors: {},
      purchaseOrders: [],
      isLoadingPos: false,
      returnableItems: [],
      isLoadingItems: false,
      isSaving: false,
      // view / delete
      showViewDialog: false,
      viewing: null,
      showDeleteDialog: false,
      toDelete: null,
      isDeleting: false,
    };
  },
  computed: {
    totals() {
      let subtotal = 0;
      let gst = 0;
      this.returnableItems.forEach((item) => {
        const line = this.lineSubtotal(item);
        subtotal += line;
        gst += this.lineGst(item, line);
      });
      return { subtotal, gst, total: subtotal + gst };
    },
    canSave() {
      const lines = this.returnableItems.filter((i) => Number(i.return_qty) > 0);
      return (
        !!this.form.purchase_order_id &&
        lines.length > 0 &&
        lines.every((i) => Number(i.return_qty) <= i.returnable_quantity) &&
        (this.form.settlement_type === 0 || this.form.refund_mode !== null)
      );
    },
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
    this.getMasters();
    // Coming from a purchase order's "Return items" action
    if (this.$route.query.purchase_order_id) {
      this.openCreate(Number(this.$route.query.purchase_order_id));
    }
  },
  methods: {
    emptyForm() {
      return {
        vendor_id: null,
        purchase_order_id: null,
        return_date: today(),
        reason: "",
        settlement_type: 0,
        refund_mode: null,
        reference_number: "",
      };
    },
    settlementLabel(ret) {
      if (ret.settlement_type === 1) {
        const mode = this.paymentModes.find((m) => m.value === ret.refund_mode);
        return "Refunded" + (mode ? ` (${mode.text})` : "");
      }
      return "Adjusted in dues";
    },
    totalQty(ret) {
      return (ret.items || []).reduce((sum, i) => sum + Number(i.quantity), 0);
    },
    userName(user) {
      if (!user) return "";
      return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.user_name || "";
    },
    variantLabel(variant) {
      if (!variant) return "";
      return [variant.color, variant.size].filter(Boolean).join(" - ");
    },
    poLabel(po) {
      return `${po.po_number} - ${po.vendor ? po.vendor.name : ""} (₹${po.total_amount})`;
    },
    lineSubtotal(item) {
      const qty = Number(item.return_qty) || 0;
      return Math.round(qty * Number(item.purchase_price) * 100) / 100;
    },
    lineGst(item, line) {
      // Same rounding as the API: nearest rupee, X.50 rounds down
      return Math.ceil(line * (Number(item.gst_percentage) || 0) / 100 - 0.5);
    },
    lineAmount(item) {
      const line = this.lineSubtotal(item);
      return line + this.lineGst(item, line);
    },
    async getMasters() {
      await axios
        .get(`masters?context=purchase_order`)
        .then((response) => {
          this.vendors = response.data.vendors || [];
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    async getData() {
      this.isLoading = true;
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&vendor_id=" +
        (this.filter.vendor_id || "") +
        "&search_keyword=" +
        (this.filter.search_keyword || "");
      await axios
        .get(`purchase_returns?${query}`)
        .then((response) => {
          this.returns = response.data.data;
          this.count = response.data.count;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },
    async loadPurchaseOrders() {
      this.isLoadingPos = true;
      await axios
        .get(`purchase_orders?status=ordered&vendor_id=${this.form.vendor_id || ""}`)
        .then((response) => {
          this.purchaseOrders = response.data.data || [];
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoadingPos = false;
    },
    async openEdit(ret) {
      this.editingId = ret.id;
      this.editingNumber = ret.return_number;
      this.form = {
        vendor_id: ret.vendor_id,
        purchase_order_id: ret.purchase_order_id,
        return_date: (ret.return_date || "").substr(0, 10),
        reason: ret.reason || "",
        settlement_type: ret.settlement_type,
        refund_mode: ret.refund_mode,
        reference_number: ret.reference_number || "",
      };
      this.errors = {};
      // The PO is fixed on an edit - only it needs to be in the list
      this.purchaseOrders = ret.purchase_order ? [{ ...ret.purchase_order, vendor: ret.vendor }] : [];
      this.showCreateDialog = true;
      this.loadReturnable();
    },
    async openCreate(purchaseOrderId = null) {
      this.editingId = null;
      this.editingNumber = "";
      this.form = this.emptyForm();
      this.errors = {};
      this.returnableItems = [];
      this.showCreateDialog = true;
      await this.loadPurchaseOrders();
      if (purchaseOrderId) {
        this.form.purchase_order_id = purchaseOrderId;
        this.loadReturnable();
      }
    },
    onVendorChange() {
      this.form.purchase_order_id = null;
      this.returnableItems = [];
      this.loadPurchaseOrders();
    },
    async loadReturnable() {
      this.returnableItems = [];
      if (!this.form.purchase_order_id) return;
      this.isLoadingItems = true;
      // When editing, the API leaves this return's own lines out of "already
      // returned" and counts its units as available again
      const exclude = this.editingId ? `?exclude_return_id=${this.editingId}` : "";
      await axios
        .get(`purchase_returns/purchase_order/${this.form.purchase_order_id}${exclude}`)
        .then((response) => {
          const order = response.data.data;
          this.form.vendor_id = order.vendor_id;
          this.returnableItems = (order.items || []).map((item) => ({
            ...item,
            return_qty: this.editingId ? item.current_quantity || 0 : 0,
          }));
        })
        .catch((error) => {
          this.notify("error", (error.response && error.response.data.message) || "Could not load the purchase order.");
        });
      this.isLoadingItems = false;
    },
    async save() {
      this.isSaving = true;
      this.errors = {};
      const payload = {
        purchase_order_id: this.form.purchase_order_id,
        return_date: this.form.return_date,
        reason: this.form.reason,
        settlement_type: this.form.settlement_type,
        refund_mode: this.form.settlement_type === 1 ? this.form.refund_mode : null,
        reference_number: this.form.reference_number,
        items: this.returnableItems
          .filter((i) => Number(i.return_qty) > 0)
          .map((i) => ({ purchase_order_item_id: i.id, quantity: Number(i.return_qty) })),
      };
      const request = this.editingId
        ? axios.patch(`purchase_returns/${this.editingId}`, payload)
        : axios.post(`purchase_returns`, payload);
      await request
        .then((response) => {
          this.showCreateDialog = false;
          this.notify(
            "success",
            this.editingId
              ? `Purchase return ${response.data.data.return_number} updated - stock adjusted`
              : `Purchase return ${response.data.data.return_number} created - stock updated`
          );
          if (!this.editingId) this.page = 1;
          this.getData();
        })
        .catch((error) => {
          const data = (error.response && error.response.data) || {};
          if (data.errors) {
            Object.keys(data.errors).forEach((key) => this.$set(this.errors, key, data.errors[key]));
          }
          this.notify("error", data.message || "Could not save the purchase return.");
        });
      this.isSaving = false;
    },
    viewReturn(ret) {
      this.viewing = ret;
      this.showViewDialog = true;
    },
    confirmDelete(ret) {
      this.toDelete = ret;
      this.showDeleteDialog = true;
    },
    async deleteReturn() {
      this.isDeleting = true;
      await axios
        .post(`purchase_returns/delete/${this.toDelete.id}`)
        .then(() => {
          this.showDeleteDialog = false;
          this.notify("success", "Purchase return deleted - stock added back");
          this.getData();
        })
        .catch((error) => {
          this.notify("error", (error.response && error.response.data.message) || "Could not delete the return.");
        });
      this.isDeleting = false;
    },
    notify(type, message) {
      if (this.$toast) this.$toast[type](message);
    },
  },
};
</script>
<style scoped>
.draft-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #fae8dd;
}
.draft-banner__icon {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: #ffa24b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.draft-banner__text {
  color: #f08a24;
  font-weight: 500;
}
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover > td {
  background-color: #FBF7EF !important;
}
</style>
